import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { STAFF } from "@/constants/testIds";
import { Users2, UserPlus, X } from "lucide-react";

export default function StaffTeams() {
  const [teams, setTeams] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [inviteId, setInviteId] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/teams/mine");
      setTeams(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name) return toast.error("Name required");
    try {
      await api.post("/teams", { name, description: desc });
      toast.success("Team created — you are now the Lead Professional");
      setName(""); setDesc("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const invite = async (teamId) => {
    const ss = inviteId[teamId];
    if (!ss) return;
    try {
      await api.post(`/teams/${teamId}/invite`, { secusource_id: ss });
      toast.success(`Invited ${ss}`);
      setInviteId({ ...inviteId, [teamId]: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-outfit font-bold text-white">Cluster Teams</h1>

      <Dialog>
        <DialogTrigger asChild>
          <Button className="bg-brand hover:bg-brand-hover text-white" data-testid={STAFF.createTeam}>
            <UserPlus className="w-4 h-4 mr-2" /> Create a Team
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Create a Cluster Team</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Team name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="team-name-input" /></div>
            <div><Label>Description</Label><Textarea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="team-desc-input" /></div>
          </div>
          <DialogFooter><Button onClick={create} className="bg-brand hover:bg-brand-hover text-white" data-testid="team-create-submit">Create Team</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid gap-4">
        {teams.length === 0 && <div className="card-flat p-8 text-center text-white/60 font-manrope">You are not part of any teams yet.</div>}
        {teams.map((t) => (
          <div key={t.id} className="card-flat p-5" data-testid={`team-card-${t.id}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="font-outfit font-semibold text-white text-lg flex items-center gap-2"><Users2 className="w-4 h-4 text-brand" />{t.name}</div>
                <div className="text-sm text-white/60 font-manrope">Lead: <span className="ss-id">{t.lead_secusource_id}</span> · {t.member_ids?.length || 0} members</div>
                {t.description && <div className="text-sm text-white/70 mt-2 font-manrope">{t.description}</div>}
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <Input
                placeholder="SS-0001"
                value={inviteId[t.id] || ""}
                onChange={(e) => setInviteId({ ...inviteId, [t.id]: e.target.value })}
                data-testid={`team-invite-input-${t.id}`}
              />
              <Button onClick={() => invite(t.id)} className="bg-brand hover:bg-brand-hover text-white" data-testid={`team-invite-btn-${t.id}`}>
                Invite Member
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
